import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check, ChevronDown } from "lucide-react";
import { fetchKnownMappings, deleteKnownMappings, fetchFieldRequirements } from "@/services/api";
import { fetchProvidersCached, fetchSchemaAssignments } from "@/services/providersApi";
import { listSchemas } from "@/services/schemaBuilderApi";
import { useUpload } from "@/hooks/useUpload";
import { useTelemetry } from "@/hooks/useTelemetry";
import { Button, AlertMessage, PageBanner, Spinner } from "@/components/reusable";
import { KnownMappingDetailPanel } from "@/components/known-mappings/KnownMappingDetailPanel";
import {
  mergeSchemaDisplayNames,
  resolveSchemaDisplayName,
  schemasForProviderFilter,
} from "@/lib/knownMappingFilters";
import { cn } from "@/lib/utils";
import type { KnownMapping, FieldRequirement } from "@/types";
import type { ProviderOut } from "@/types/providers";

/**
 * Known mappings browser.
 *
 * When `providerId` is set (provider profile / upload dialog), the page is
 * server-scoped to that provider: the Provider filter and column are hidden.
 * Otherwise filters run client-side in three stages — schema pills → provider
 * multi-select → per-schema report_type — and a row-click opens the detail panel.
 */
export function KnownMappingsPage({ providerId }: { providerId?: string } = {}) {
  const { t } = useTranslation();
  const { savedMappings, saveMappings } = useUpload();
  const { track, trackError, startTimer } = useTelemetry();

  const isProviderScoped = Boolean(providerId);

  type SortField = "source" | "target" | "schema" | "provider";
  const [sort, setSort] = useState<{ field: SortField; direction: "asc" | "desc" }>({
    field: "target",
    direction: "asc",
  });

  const [apiMappings, setApiMappings] = useState<KnownMapping[]>([]);
  const [fieldRequirements, setFieldRequirements] = useState<FieldRequirement[]>([]);
  const [providers, setProviders] = useState<ProviderOut[]>([]);
  /** schema_id → human name from assignments (scoped) or listSchemas (unscoped). */
  const [catalogueNameById, setCatalogueNameById] = useState<Map<string, string>>(new Map());
  // Schema pills filter by kodex_schemas.schema_id. When providers are selected,
  // the pill list is scoped to schemas present on those providers' mappings.
  const [selectedSchemaIds, setSelectedSchemaIds] = useState<string[]>([]);
  // Per-schema selected report type; absence or "All" means no report_type filter for that schema.
  const [selectedReportTypeBySchemaId, setSelectedReportTypeBySchemaId] = useState<
    Record<string, string>
  >({});
  // Bound to kodex_known_mappings.provider_id; ignored when providerId already scopes the page.
  const [selectedProviderIds, setSelectedProviderIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [deleteAlert, setDeleteAlert] = useState<{
    variant: "success" | "error";
    message: string;
  } | null>(null);
  const [detailMapping, setDetailMapping] = useState<KnownMapping | null>(null);

  useEffect(() => {
    const stopTimer = startTimer();
    // Server-side scope when embedded under a single provider (profile tab / upload dialog).
    Promise.all([fetchKnownMappings(undefined, providerId), fetchFieldRequirements()])
      .then(([mappings, requirements]) => {
        setApiMappings(mappings);
        setFieldRequirements(requirements);
        track("known_mappings_loaded", {
          mappingCount: mappings.length,
          fieldRequirementCount: requirements.length,
          durationMs: stopTimer(),
          providerId: providerId ?? "all",
        });
      })
      .catch((err) => trackError(err, { context: "known_mappings_load" }))
      .finally(() => setLoading(false));
  }, [startTimer, track, trackError, providerId]);

  // Fresh catalogue for UUID → name (provider assignments when scoped; else all schemas).
  useEffect(() => {
    let cancelled = false;
    const load = providerId
      ? fetchSchemaAssignments(providerId).then((assignments) => {
          const map = new Map<string, string>();
          for (const a of assignments) {
            const name = (a.schema_name ?? a.schema_id)?.trim();
            if (a.schema_id && name) map.set(a.schema_id, name);
          }
          return map;
        })
      : listSchemas(true).then((schemas) => {
          const map = new Map<string, string>();
          for (const s of schemas) {
            const name = s.name?.trim();
            if (s.id && name) map.set(s.id, name);
          }
          return map;
        });

    load
      .then((map) => {
        if (!cancelled) setCatalogueNameById(map);
      })
      .catch((err) => trackError(err as Error, { context: "known_mappings_schema_names_load" }));

    return () => {
      cancelled = true;
    };
  }, [providerId, trackError]);

  // Providers power the real "Provider" column/filter -- only fetched when the
  // page is not already scoped to a single provider.
  useEffect(() => {
    if (isProviderScoped) return;
    fetchProvidersCached()
      .then((res) => setProviders(res.items))
      .catch((err) => trackError(err as Error, { context: "known_mappings_providers_load" }));
  }, [trackError, isProviderScoped]);

  /** id → display label for filter / column / detail. Prefer name, then short code. */
  const providerNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of providers) {
      const label = p.provider_name || p.provider_short_code;
      if (label) map.set(p.provider_id, label);
    }
    return map;
  }, [providers]);

  // Merge API mappings with user-saved mappings from the workflow.
  // When provider-scoped, only merge session mappings that belong to this provider
  // (or have no providerId yet — local-only until persisted).
  const mappings = useMemo(() => {
    const merged = [...apiMappings];
    const existingKeys = new Set(
      apiMappings.map(
        (m) => `${m.sourceField}::${m.targetField}::${m.schemaId}::${m.providerId ?? ""}`
      )
    );
    for (const sm of savedMappings) {
      if (isProviderScoped && sm.providerId && sm.providerId !== providerId) {
        continue;
      }
      const key = `${sm.sourceField}::${sm.targetField}::${sm.schemaId}::${sm.providerId ?? ""}`;
      if (!existingKeys.has(key)) {
        merged.push(sm);
      }
    }
    return merged;
  }, [apiMappings, savedMappings, isProviderScoped, providerId]);

  // Schema pills shrink to schemas that have mappings for the selected providers.
  // Catalogue/assignment names overlay UUID fallbacks from mapping rows.
  const schemaDisplayNameById = useMemo(
    () =>
      mergeSchemaDisplayNames(
        schemasForProviderFilter(mappings, selectedProviderIds),
        catalogueNameById
      ),
    [mappings, selectedProviderIds, catalogueNameById]
  );

  const schemaFilterOptions = useMemo(
    () =>
      [...schemaDisplayNameById.keys()].sort((a, b) =>
        (schemaDisplayNameById.get(a) ?? a).localeCompare(schemaDisplayNameById.get(b) ?? b)
      ),
    [schemaDisplayNameById]
  );

  // Drop schema selections (and their report-type filters) that are no longer in the pill list.
  useEffect(() => {
    const available = new Set(schemaFilterOptions);
    setSelectedSchemaIds((prev) => {
      const next = prev.filter((id) => available.has(id));
      return next.length === prev.length ? prev : next;
    });
    setSelectedReportTypeBySchemaId((prev) => {
      const entries = Object.entries(prev).filter(([schemaId]) => available.has(schemaId));
      if (entries.length === Object.keys(prev).length) return prev;
      return Object.fromEntries(entries);
    });
  }, [schemaFilterOptions]);

  /** Distinct providers actually present in the current mapping set, for the filter dropdown. */
  const providerFilterOptions = useMemo(() => {
    const ids = new Set<string>();
    for (const m of mappings) {
      if (m.providerId) ids.add(m.providerId);
    }
    return [...ids].sort((a, b) =>
      (providerNameById.get(a) ?? a).localeCompare(providerNameById.get(b) ?? b)
    );
  }, [mappings, providerNameById]);

  /**
   * Nested lookup: schemaId → reportType → Set<fieldId>.
   * Built from klyra_field_requirements data fetched on mount.
   * Used to filter known mappings by (schema_id, field_id) when a report_type is chosen.
   */
  const fieldsBySchemaAndReportType = useMemo(() => {
    const outer = new Map<string, Map<string, Set<string>>>();
    for (const req of fieldRequirements) {
      if (!outer.has(req.schemaId)) outer.set(req.schemaId, new Map());
      const inner = outer.get(req.schemaId)!;
      if (!inner.has(req.reportType)) inner.set(req.reportType, new Set());
      inner.get(req.reportType)!.add(req.fieldId);
    }
    return outer;
  }, [fieldRequirements]);

  /**
   * Distinct, sorted report type options per schema — derived from fieldsBySchemaAndReportType.
   * Used to populate the per-schema report_type dropdown in the UI.
   */
  const reportTypesBySchemaId = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const [schemaId, reportTypeMap] of fieldsBySchemaAndReportType) {
      map.set(schemaId, [...reportTypeMap.keys()].sort());
    }
    return map;
  }, [fieldsBySchemaAndReportType]);

  // Stage 1: filter by selected schema pills.
  const schemaFiltered = selectedSchemaIds.length
    ? mappings.filter((m) => selectedSchemaIds.includes(m.schemaId))
    : mappings;

  // Stage 2: filter by selected providers.
  const providerFiltered = selectedProviderIds.length
    ? schemaFiltered.filter((m) => !!m.providerId && selectedProviderIds.includes(m.providerId))
    : schemaFiltered;

  /**
   * Mapping counts per (schemaId, reportType), used to show a count badge on each pill.
   * "All" count is the total number of mappings for that schema after the schema+provider filters.
   */
  const mappingCountBySchemaAndReportType = useMemo(() => {
    const counts = new Map<string, Map<string, number>>();
    for (const m of providerFiltered) {
      if (!counts.has(m.schemaId)) counts.set(m.schemaId, new Map());
      const inner = counts.get(m.schemaId)!;
      // Always increment "All" for this schema.
      inner.set("All", (inner.get("All") ?? 0) + 1);
      // Increment each report_type this mapping's targetFieldId belongs to.
      const reportTypeMap = fieldsBySchemaAndReportType.get(m.schemaId);
      if (reportTypeMap) {
        for (const [rt, fieldIds] of reportTypeMap) {
          if (fieldIds.has(m.targetFieldId ?? "")) {
            inner.set(rt, (inner.get(rt) ?? 0) + 1);
          }
        }
      }
    }
    return counts;
  }, [providerFiltered, fieldsBySchemaAndReportType]);

  /**
   * Stage 3: filter by selected report_type per schema.
   * Only hides rows when a specific report_type (not "All") is selected for that schema.
   * The join key is (schemaId, targetFieldId) — schema-scoped to prevent cross-schema collisions.
   */
  const filtered = providerFiltered.filter((m) => {
    const selectedReportType = selectedReportTypeBySchemaId[m.schemaId] ?? "All";
    if (selectedReportType === "All") return true;
    const allowedFieldIds = fieldsBySchemaAndReportType.get(m.schemaId)?.get(selectedReportType);
    return allowedFieldIds ? allowedFieldIds.has(m.targetFieldId ?? "") : false;
  });

  const sorted = useMemo(() => {
    const getValue = (m: KnownMapping): string => {
      switch (sort.field) {
        case "source":
          return m.sourceField ?? "";
        case "target":
          return m.targetField ?? "";
        case "schema":
          return resolveSchemaDisplayName(m.schemaId, m.schemaName, schemaDisplayNameById);
        case "provider":
          return m.providerId ? (providerNameById.get(m.providerId) ?? m.providerId) : "";
        default:
          return "";
      }
    };

    const dir = sort.direction === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      const cmp = av.localeCompare(bv, undefined, { sensitivity: "base", numeric: true });
      return cmp === 0 ? a.mappingId.localeCompare(b.mappingId) : cmp * dir;
    });
  }, [filtered, sort.direction, sort.field, providerNameById, schemaDisplayNameById]);

  const allFilteredSelected =
    sorted.length > 0 && sorted.every((m) => selectedIds.has(m.mappingId));
  const someSelected = selectedIds.size > 0;

  const toggleSchema = useCallback(
    (schemaId: string) => {
      setSelectedSchemaIds((prev) => {
        const next = prev.includes(schemaId)
          ? prev.filter((x) => x !== schemaId)
          : [...prev, schemaId];
        track("known_mappings_provider_filter_toggled", {
          provider: schemaId,
          active: !prev.includes(schemaId),
          activeProviderCount: next.length,
        });
        return next;
      });
      // Reset report_type selection when a schema is deselected so stale filters don't persist.
      setSelectedReportTypeBySchemaId((prev) => {
        if (!selectedSchemaIds.includes(schemaId)) return prev;
        const next = { ...prev };
        delete next[schemaId];
        return next;
      });
    },
    [track, selectedSchemaIds]
  );

  const toggleProviderFilter = useCallback(
    (providerId: string) => {
      setSelectedProviderIds((prev) => {
        const next = prev.includes(providerId)
          ? prev.filter((x) => x !== providerId)
          : [...prev, providerId];
        track("known_mappings_real_provider_filter_toggled", {
          providerId,
          active: !prev.includes(providerId),
          activeProviderCount: next.length,
        });
        return next;
      });
    },
    [track]
  );

  /**
   * Sets the active report_type filter for a specific schema.
   * Passing "All" (or omitting) clears the filter for that schema.
   */
  const selectReportType = useCallback(
    (schemaId: string, reportType: string) => {
      setSelectedReportTypeBySchemaId((prev) => {
        if (reportType === "All") {
          const next = { ...prev };
          delete next[schemaId];
          return next;
        }
        return { ...prev, [schemaId]: reportType };
      });
      track("known_mappings_report_type_filter_selected", { schemaId, reportType });
    },
    [track]
  );

  const toggleRow = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (sorted.every((m) => prev.has(m.mappingId))) {
        const next = new Set(prev);
        sorted.forEach((m) => next.delete(m.mappingId));
        return next;
      }
      const next = new Set(prev);
      sorted.forEach((m) => next.add(m.mappingId));
      return next;
    });
  }, [sorted]);

  const toggleSort = useCallback(
    (field: SortField) => {
      setSort((prev) => {
        if (prev.field !== field) return { field, direction: "asc" };
        return { field, direction: prev.direction === "asc" ? "desc" : "asc" };
      });
      track("known_mappings_sort_toggled", { field });
    },
    [track]
  );

  const sortIndicator = useCallback(
    (field: SortField) => {
      if (sort.field !== field) return "";
      return sort.direction === "asc" ? " ▲" : " ▼";
    },
    [sort.direction, sort.field]
  );

  const handleDeleteSelected = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    try {
      await deleteKnownMappings(Array.from(selectedIds));
      setApiMappings((prev) => prev.filter((m) => !selectedIds.has(m.mappingId)));
      saveMappings(savedMappings.filter((m) => !selectedIds.has(m.mappingId)));
      track("known_mappings_deleted", { count: selectedIds.size });
      setSelectedIds(new Set());
      setDeleteAlert({
        variant: "success",
        message: `${selectedIds.size} mapping${selectedIds.size === 1 ? "" : "s"} deleted successfully.`,
      });
    } catch (err) {
      trackError(err as Error, { context: "known_mappings_delete" });
      setDeleteAlert({ variant: "error", message: "Failed to delete mappings. Please try again." });
    } finally {
      setDeleting(false);
      setTimeout(() => setDeleteAlert(null), 4000);
    }
  }, [selectedIds, savedMappings, saveMappings, track, trackError]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 animate-fade-in">
        <Spinner size="lg" />
        <p className="text-sm text-[var(--color-text-secondary)]">Loading known mappings...</p>
      </div>
    );
  }

  return (
    <>
      <PageBanner title={t("knownMappings.title")} subtitle={t("knownMappings.subtitle")} />

      {/* Global (cross-provider) view only — not shown on provider-scoped embeds. */}
      {!isProviderScoped && (
        <div className="mb-4 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg-secondary)] px-4 py-3 text-sm text-[var(--color-text-secondary)] animate-fade-in">
          This page is only viewable to admins and provider viewers so they can inspect known
          mappings across providers.
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-start justify-between gap-4 animate-fade-in-up stagger-2">
        {/* Schema pills */}
        <div className="flex-1 min-w-[240px]">
          <label className="text-sm font-semibold text-[var(--color-text-primary)] mb-2 block">
            {t("knownMappings.selectSchemas")}
          </label>
          <div className="mb-2 text-sm text-[var(--color-text-secondary)]">
            {t("knownMappings.pillHint")}
          </div>
          <div className="flex flex-wrap gap-2">
            {schemaFilterOptions.map((schemaId) => (
              <button
                key={schemaId}
                onClick={() => toggleSchema(schemaId)}
                className={`px-3 py-1.5 text-sm rounded-full border transition-all duration-200 active:scale-[0.96] ${
                  selectedSchemaIds.includes(schemaId)
                    ? "bg-[var(--color-button-primary-bg)] text-white border-[var(--color-button-primary-bg)] shadow-sm"
                    : "bg-white text-[var(--color-text-secondary)] border-[var(--color-border)] hover:border-[var(--color-brand-primary)] hover:shadow-sm"
                }`}
              >
                {schemaDisplayNameById.get(schemaId) ?? schemaId}
              </button>
            ))}
          </div>
        </div>

        {/* Provider multi-select — hidden when page is already scoped to a single provider. */}
        {!isProviderScoped && providerFilterOptions.length > 0 && (
          <div>
            <label className="text-sm font-semibold text-[var(--color-text-primary)] mb-2 block">
              {t("knownMappings.filterByProvider")}
            </label>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  className={cn(
                    "flex h-10 min-w-[220px] items-center justify-between gap-2 rounded-md border px-3 text-sm outline-none transition",
                    "border-[var(--color-border)] hover:border-[var(--color-brand-primary)]"
                  )}
                >
                  {selectedProviderIds.length === 0 ? (
                    <span className="text-[var(--color-text-tertiary)]">
                      {t("knownMappings.allProviders")}
                    </span>
                  ) : (
                    <span className="truncate text-[var(--color-text-primary)]">
                      {selectedProviderIds.map((id) => providerNameById.get(id) ?? id).join(", ")}
                    </span>
                  )}
                  <ChevronDown className="h-4 w-4 shrink-0 text-[var(--color-text-tertiary)]" />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  className="z-50 min-w-[var(--radix-dropdown-menu-trigger-width)] max-h-60 overflow-y-auto rounded-lg border border-[var(--color-border)] bg-white p-1 shadow-lg animate-in fade-in-0 zoom-in-95"
                  sideOffset={4}
                  align="start"
                >
                  {providerFilterOptions.map((providerId) => {
                    const checked = selectedProviderIds.includes(providerId);
                    return (
                      <DropdownMenu.CheckboxItem
                        key={providerId}
                        checked={checked}
                        onCheckedChange={() => toggleProviderFilter(providerId)}
                        className="flex cursor-pointer select-none items-center gap-2 rounded-md px-3 py-2.5 text-sm outline-none data-[highlighted]:bg-[var(--color-bg-secondary)]"
                      >
                        <DropdownMenu.ItemIndicator>
                          <Check className="h-4 w-4 text-[var(--color-brand-primary)]" />
                        </DropdownMenu.ItemIndicator>
                        {!checked && <span className="w-4" />}
                        {providerNameById.get(providerId) ?? providerId}
                      </DropdownMenu.CheckboxItem>
                    );
                  })}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        )}
      </div>

      {/* Per-schema report type filters — only shown when at least one schema is selected */}
      {selectedSchemaIds.length > 0 && (
        <div className="mb-6 animate-fade-in-up stagger-3">
          <div className="flex flex-wrap gap-6">
            {selectedSchemaIds.map((schemaId) => {
              const reportTypes = reportTypesBySchemaId.get(schemaId) ?? [];
              if (reportTypes.length === 0) return null;
              const activeReportType = selectedReportTypeBySchemaId[schemaId] ?? "All";
              return (
                <div key={schemaId}>
                  <label className="text-xs font-semibold text-[var(--color-text-secondary)] mb-1.5 block">
                    {schemaDisplayNameById.get(schemaId) ?? schemaId}
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {["All", ...reportTypes].map((rt) => {
                      const count = mappingCountBySchemaAndReportType.get(schemaId)?.get(rt) ?? 0;
                      return (
                        <button
                          key={rt}
                          onClick={() => selectReportType(schemaId, rt)}
                          className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md border transition-all duration-150 active:scale-[0.96] ${
                            activeReportType === rt
                              ? "bg-[var(--color-brand-primary)]/10 text-[var(--color-brand-primary)] border-[var(--color-brand-primary)] font-medium"
                              : "bg-white text-[var(--color-text-secondary)] border-[var(--color-border)] hover:border-[var(--color-brand-primary)]"
                          }`}
                        >
                          {rt}
                          <span
                            className={`inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] rounded-full text-[10px] font-semibold px-1 ${
                              activeReportType === rt
                                ? "bg-[var(--color-brand-primary)] text-white"
                                : "bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)]"
                            }`}
                          >
                            {count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Table */}
      {sorted.length > 0 ? (
        <div className="rounded-xl border border-[var(--color-border-light)] overflow-hidden animate-fade-in-up stagger-3">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-bg-secondary)]">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleSelectAll}
                    className="rounded border-[var(--color-border)] accent-[var(--color-button-primary-bg)] cursor-pointer"
                    aria-label="Select all"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--color-text-secondary)]">
                  <button
                    type="button"
                    onClick={() => toggleSort("source")}
                    className="hover:text-[var(--color-text-primary)]"
                  >
                    {t("knownMappings.sourceColumn")}
                    {sortIndicator("source")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--color-text-secondary)]">
                  <button
                    type="button"
                    onClick={() => toggleSort("target")}
                    className="hover:text-[var(--color-text-primary)]"
                  >
                    {t("knownMappings.targetColumn")}
                    {sortIndicator("target")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--color-text-secondary)]">
                  <button
                    type="button"
                    onClick={() => toggleSort("schema")}
                    className="hover:text-[var(--color-text-primary)]"
                  >
                    {t("knownMappings.schemaColumn")}
                    {sortIndicator("schema")}
                  </button>
                </th>
                {!isProviderScoped && (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--color-text-secondary)]">
                    <button
                      type="button"
                      onClick={() => toggleSort("provider")}
                      className="hover:text-[var(--color-text-primary)]"
                    >
                      {t("knownMappings.providerColumn")}
                      {sortIndicator("provider")}
                    </button>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {sorted.map((m, i) => (
                <tr
                  key={m.mappingId}
                  onClick={() => setDetailMapping(m)}
                  className={`border-t border-[var(--color-border-light)] transition-colors cursor-pointer ${
                    selectedIds.has(m.mappingId)
                      ? "bg-[var(--color-brand-primary)]/5"
                      : "hover:bg-[var(--color-bg-secondary)]"
                  }`}
                  style={{
                    animation: "fade-in 0.3s ease-out both",
                    animationDelay: `${i * 25}ms`,
                  }}
                >
                  <td className="px-4 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(m.mappingId)}
                      onChange={() => toggleRow(m.mappingId)}
                      className="rounded border-[var(--color-border)] accent-[var(--color-button-primary-bg)] cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3 font-medium">{m.sourceField}</td>
                  <td className="px-4 py-3">{m.targetField}</td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {resolveSchemaDisplayName(m.schemaId, m.schemaName, schemaDisplayNameById)}
                  </td>
                  {!isProviderScoped && (
                    <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                      {m.providerId ? (providerNameById.get(m.providerId) ?? m.providerId) : "—"}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-[var(--color-text-tertiary)] text-center py-8">{t("common.noData")}</p>
      )}

      {deleteAlert && (
        <div className="mt-4 animate-fade-in">
          <AlertMessage variant={deleteAlert.variant} message={deleteAlert.message} />
        </div>
      )}

      {someSelected && (
        <div className="mt-4 animate-fade-in-up stagger-4">
          <Button onClick={handleDeleteSelected} disabled={deleting} variant="danger">
            {deleting
              ? t("common.deleting")
              : t("knownMappings.deleteSelected", { count: selectedIds.size })}
          </Button>
        </div>
      )}

      <KnownMappingDetailPanel
        mapping={detailMapping}
        onOpenChange={(open) => !open && setDetailMapping(null)}
        providerNameById={providerNameById}
        schemaDisplayNameById={schemaDisplayNameById}
      />
    </>
  );
}
